import React, {
    useRef,
    useEffect,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { filter } from 'rxjs/operators';
import _isEqual from 'lodash/isEqual';

import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import Wrapper from 'bdvp-common/src/components/PlaybackWrapper';
import { normalizeMediaItem } from 'bdvp-common/src/utils';

import { useMediaConfigReel } from 'bdvp-common/src/hooks/config';

const PlaybackWrapper = styled(Wrapper)`
    margin: 0 -18px;

    ${media.mdmin`
        width: 100%;
        margin: 0;
        height: 100%;

        .media-player {
            padding-bottom: unset;
            height: 100%;
            margin: auto;
        }
    `}

    .overlay {
        display: none;
    }
`;

const Player = (props) => {

    const {
        mediaPlayback,
        setMediaPlayer,
        setPlayhead,
        startTime,
        endTime,
        setPlayerState,
        wasPlaying,
        playerState,
        playhead,
        segments,
        roundedSegments,
        getRoundedSegment,
        isSliding,
    } = props;

    const {
        player: playerConfig = {},
    } = useMediaConfigReel();

    const subscriptions = useRef([]);
    const playerStateRef = useRef(playerState);
    const startTimeRef = useRef(startTime);
    const endTimeRef = useRef(endTime);
    const wasPlayingRef = useRef(wasPlaying);
    const playheadRef = useRef(playhead);

    useEffect(() => {

        return (() => {

            subscriptions.current.forEach((subscription) => {

                subscription.unsubscribe();

            });

        });

    }, []);

    useEffect(() => {

        playerStateRef.current = playerState;

    }, [playerState]);

    useEffect(() => {

        startTimeRef.current = startTime;

    }, [startTime]);

    useEffect(() => {

        endTimeRef.current = endTime;

    }, [endTime]);

    useEffect(() => {

        wasPlayingRef.current = wasPlaying;

    }, [wasPlaying]);

    useEffect(() => {

        playheadRef.current = playhead;

    }, [playhead]);

    const {
        initConfig = {},
        mediaConfig = {},
        playerOptions = {},
    } = playerConfig;

    return (
        <PlaybackWrapper
            playbackContainerId={'reel-clip-trimming-player'}
            autoPlay={false}
            initConfig={initConfig}
            handleMediaPlayerReady={setMediaPlayer}
            handleEmbedWrapper={(embedWrapper) => {

                const videoWrapperEl = embedWrapper.context;
                if(!videoWrapperEl) {

                    return;

                }

                // set tabIndex to have the user interact with the player using the custom controls
                videoWrapperEl.tabIndex = -1;

            }}
            mediaConfig={{
                ...mediaConfig,
                playerTypeConfig: {
                    ...mediaConfig.playerTypeConfig || {},
                    controls: false,
                },
                controlsConfig: {
                    ...mediaConfig.controlsConfig || {},
                    showAdControls: false,
                    showDesktopControls: false,
                },
            }}
            playerOptions={{
                ...playerOptions || {},
                metaData: normalizeMediaItem(mediaPlayback),
            }}
            handleObservable={(observationCenter) => {

                subscriptions.current.push(

                    observationCenter.playerSubject.pipe(filter((event) => {

                        return event.type === 'ended';

                    })).subscribe((data) => {

                        data.event.target.currentTime = segments[roundedSegments.indexOf(startTimeRef.current)];
                        if (wasPlayingRef.current) {

                            data.event.target.play();

                        }

                    })
                );

                // update player's current time on play in case playing video fails
                // seek video after play on ios devices.
                // ios devices require you to wait for the initial playing event before seeking
                // seek video after play if playback scenario is mp4
                subscriptions.current.push(
                    observationCenter.playerSubject.pipe(
                        filter((event) => {

                            return event.type === 'play';

                        })
                    ).subscribe((data) => {

                        data.event.target.currentTime = segments[roundedSegments.indexOf(playheadRef.current)] || playheadRef.current;

                    })
                );

                // if hls.js, then seek video before playing.
                // hlsManifestParsed does not trigger on ios
                subscriptions.current.push(
                    observationCenter.playerSubject.pipe(filter((event) => {

                        return event.type === 'hlsManifestParsed';

                    })).subscribe((data) => {

                        data.mediaPlayer.hls.startLoad(startTimeRef.current);

                    })
                );

                subscriptions.current.push(
                    observationCenter.playerSubject.pipe(
                        filter((event) => event.type === 'timeupdate')
                    ).subscribe((data) => {

                        let currentTime = data.event.target.currentTime;

                        if(currentTime === 0) {

                            return;

                        }

                        const roundedTime = getRoundedSegment(currentTime);

                        if (
                            roundedTime < startTimeRef.current
                            || roundedTime >= endTimeRef.current
                        ) {

                            currentTime = segments[roundedSegments.indexOf(startTimeRef.current)];
                            data.event.target.currentTime = currentTime;

                        }

                        if (!isSliding.current) {

                            setPlayhead(currentTime);

                        }

                    })
                );

                subscriptions.current.push(
                    observationCenter.stateSubject.subscribe((data) => {

                        if (data.type && data.type !== 'empty' && data.stateModel) {

                            const updatedPlayerState = {
                                paused: data.stateModel.paused,
                                volume: data.stateModel.volume.value,
                                muted: data.stateModel.volume.muted,
                                lastVolume: data.stateModel.volume.lastValue,
                            };

                            if(!_isEqual(playerStateRef.current, updatedPlayerState)) {

                                setPlayerState(updatedPlayerState);

                            }

                        };

                    })
                );

            }}
        />
    );

};

Player.propTypes = {
    mediaPlayback: PropTypes.object.isRequired,
    setPlayhead: PropTypes.func.isRequired,
    setPlayerState: PropTypes.func.isRequired,
    startTime: PropTypes.number.isRequired,
    endTime: PropTypes.number.isRequired,
    wasPlaying: PropTypes.bool.isRequired,
    isSliding: PropTypes.shape({
        current: PropTypes.bool.isRequired,
    }).isRequired,
    setMediaPlayer: PropTypes.func.isRequired,
    playerState: PropTypes.shape({
        paused: PropTypes.bool.isRequired,
        volume: PropTypes.number.isRequired,
        muted: PropTypes.bool.isRequired,
        lastVolume: PropTypes.number.isRequired,
    }).isRequired,
    playhead: PropTypes.number.isRequired,
};

export default Player;
